import React, { useEffect, useState } from 'react';
import { StudentTestimonial } from '../../types';
import { studentTestimonialApi } from '../../services/api';
import Modal from '../../components/Modal';
import { PencilIcon, TrashIcon, PlusCircleIcon } from '../../components/icons';

const ManageStudentTestimoniesPage: React.FC = () => {
  const [testimonies, setTestimonies] = useState<StudentTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTestimony, setCurrentTestimony] = useState<Partial<StudentTestimonial> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTestimonies();
  }, []);

  const fetchTestimonies = async () => {
    try {
      setLoading(true);
      const response = await studentTestimonialApi.getAll();
      setTestimonies(response.data.data.testimonies);
      setError(null);
    } catch (err) {
      setError('Failed to load testimonies.');
    } finally {
      setLoading(false);
    }
  };

  const openModalForNew = () => {
    setIsEditing(false);
    setCurrentTestimony({ studentName: '', text: '', videoUrl: '' });
    setIsModalOpen(true);
  };

  const openModalForEdit = (testimony: StudentTestimonial) => {
    setIsEditing(true);
    setCurrentTestimony({ ...testimony });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentTestimony(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this testimony?')) return;
    try {
      await studentTestimonialApi.delete(id);
      await fetchTestimonies();
    } catch (err) {
      alert('Failed to delete testimony.');
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!currentTestimony) return;
    const { name, value } = e.target;
    setCurrentTestimony({ ...currentTestimony, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTestimony) return;
    setSaving(true);
    try {
      if (isEditing && currentTestimony._id) {
        await studentTestimonialApi.update(currentTestimony._id, currentTestimony);
      } else {
        await studentTestimonialApi.create(currentTestimony);
      }
      await fetchTestimonies();
      closeModal();
    } catch (err) {
      alert('Failed to save testimony.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-white">Student Testimonies</h2>
        <button
          onClick={openModalForNew}
          className="flex items-center bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          <PlusCircleIcon className="w-5 h-5 mr-2" />
          Add Testimony
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center min-h-[100px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
        </div>
      ) : (
        <div className="bg-neutral-dark shadow-xl rounded-lg overflow-x-auto">
          <table className="w-full text-sm text-left text-neutral-300">
            <thead className="text-xs text-neutral-100 uppercase bg-neutral-darker">
              <tr>
                <th className="px-6 py-3">Student Name</th>
                <th className="px-6 py-3">Text</th>
                <th className="px-6 py-3">Video</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {testimonies.map((t) => (
                <tr key={t._id} className="bg-neutral-dark border-b border-neutral-700 hover:bg-neutral-dark/70">
                  <td className="px-6 py-4 font-medium text-white whitespace-nowrap">{t.studentName}</td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="text-neutral-light max-h-20 overflow-y-auto custom-scrollbar pr-2">
                        {t.text || <span className="text-neutral-400 italic">No text provided</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {t.videoUrl ? (
                      <div className="relative w-40 h-24">
                        {t.videoUrl.includes('youtube.com') || t.videoUrl.includes('youtu.be') ? (
                          <iframe
                            className="absolute top-0 left-0 w-full h-full rounded shadow"
                            src={t.videoUrl.replace('watch?v=', 'embed/')}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video controls className="absolute top-0 left-0 w-full h-full rounded shadow object-cover">
                            <source src={t.videoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </div>
                    ) : (
                      <span className="text-neutral-400 italic">No video</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button 
                      onClick={() => openModalForEdit(t)} 
                      className="text-primary-light hover:text-primary p-2 rounded-lg hover:bg-primary/10 transition-colors"
                      title="Edit"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleDelete(t._id)} 
                      className="text-red-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isModalOpen && currentTestimony && (
        <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditing ? 'Edit Testimony' : 'Add Testimony'}>
          <form onSubmit={handleSubmit} className="space-y-6 text-neutral-light">
            <div>
              <label htmlFor="studentName" className="block text-sm font-medium mb-2">Student Name</label>
              <input 
                type="text" 
                name="studentName" 
                id="studentName" 
                value={currentTestimony.studentName || ''} 
                onChange={handleFormChange} 
                required 
                className="w-full bg-neutral-darker p-3 rounded-lg border border-neutral-600 focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                placeholder="Enter student's name"
              />
            </div>
            <div>
              <label htmlFor="text" className="block text-sm font-medium mb-2">Testimonial Text</label>
              <textarea 
                name="text" 
                id="text" 
                value={currentTestimony.text || ''} 
                onChange={handleFormChange} 
                rows={4} 
                className="w-full bg-neutral-darker p-3 rounded-lg border border-neutral-600 focus:ring-2 focus:ring-primary focus:border-primary transition-colors custom-scrollbar"
                placeholder="Enter student's testimonial text"
              ></textarea>
              <p className="text-xs text-neutral-400 mt-1">You can provide text, video, or both for the testimonial.</p>
            </div>
            <div>
              <label htmlFor="videoUrl" className="block text-sm font-medium mb-2">Video URL</label>
              <input 
                type="text" 
                name="videoUrl" 
                id="videoUrl" 
                value={currentTestimony.videoUrl || ''} 
                onChange={handleFormChange} 
                className="w-full bg-neutral-darker p-3 rounded-lg border border-neutral-600 focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                placeholder="Enter video URL (YouTube or direct MP4 link)"
              />
              <p className="text-xs text-neutral-400 mt-1">Supports YouTube URLs or direct MP4 video links.</p>
            </div>
            <div className="flex justify-end space-x-3 pt-6 border-t border-neutral-700">
              <button 
                type="button" 
                onClick={closeModal} 
                className="px-4 py-2 rounded-lg bg-neutral-600 hover:bg-neutral-500 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white transition-colors flex items-center"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  isEditing ? 'Save Changes' : 'Add Testimony'
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ManageStudentTestimoniesPage; 